import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import PlusIcon from "@assets/icons/plus.svg";

import { useEscapeKey } from "@hooks/useEscapeKey";
import { TagsTypes } from "@components/Sections/TagsSection/constants";
import { cn } from "@utils/tailwind.util";
import { sortTagsByOrder } from "@utils/tags.util";
import { Tag } from "@interfaces/tag.interface";
import { ITagResponse } from "@interfaces/responses.interface";
import { IRelationResponse } from "@interfaces/relation.interface";

import { TaggingComponent } from "../TaggingComponent/TaggingComponent";
import { Tag as TagItem } from "../Tags/Tag";
import { View } from "@ui/View";
import { Button } from "@ui/Button";

interface Props {
  contentType?: string;
  keyForTypes: keyof typeof TagsTypes;
  onRemoveTag: (value: ITagResponse) => void;
  onChangeTags: (value: Tag[]) => void;
  selectedTags: ITagResponse[];
  temporaryTags: IRelationResponse[];
  excludedIds: string[];
  isDropdownOpened: boolean;
  onCloseDropdown: () => void;
  onOpenDropdown?: () => void;
  dropdownClassNames?: string;
}

export const TaggingComponentWithTags = ({
  contentType,
  keyForTypes,
  onRemoveTag,
  onChangeTags,
  selectedTags,
  temporaryTags,
  excludedIds,
  isDropdownOpened,
  onCloseDropdown,
  onOpenDropdown,
  dropdownClassNames = "",
}: Props) => {
  const renderTags = useCallback(
    (
      value: ITagResponse[],
      onAdd: (value: Tag[]) => void,
      onRemove: (value: ITagResponse) => void
    ) => {
      const tags: ITagResponse[] = value.filter(
        (tag: ITagResponse) => !(tag.object_type === "notes.ainote" && tag.state === "trashed")
      );
      const handleAdd = (tag: ITagResponse) => onAdd([tag as unknown as Tag]);

      const sortedTags = sortTagsByOrder(tags);
      return sortedTags.map((tag) => {
        const isWithinTemporaryTags = temporaryTags.some((t) => t.right_object_id === tag.id);
        // NOTE: both conditions are required to hide the (x) icon because:
        // - tag.pending reflects the tag's creation status (still being added)
        // - isWithinTemporaryTags shows whether the tag's relation is in a temporary state (e.g., just added, not fully confirmed)
        const canRemove = !tag.pending && !isWithinTemporaryTags && !tag?.is_suggested;
        return (
          <TagItem
            key={tag.id}
            data={tag}
            onAdd={tag?.is_suggested ? handleAdd : undefined}
            onRemove={canRemove ? onRemove : undefined}
          />
        );
      });
    },
    [temporaryTags]
  );

  const [query, setQuery] = useState("");
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const showDropdown = isDropdownOpened && !!dropdownPosition && portalRoot;

  const onSelect = (item: Tag) => {
    setTimeout(() => {
      const newValue = [item];

      onChangeTags(newValue);
      onCloseDropdown();
      setQuery("");
    }, 0);
  };

  useEscapeKey({ callback: () => onCloseDropdown(), enabled: isDropdownOpened });

  useEffect(() => {
    const handleClickOutside = (event: PointerEvent) => {
      const target = event.target as Node;

      if (dropdownRef.current?.contains(target)) return;

      if (isDropdownOpened && dropdownRef.current && !dropdownRef.current.contains(target)) {
        onCloseDropdown();
      }
    };

    if (isDropdownOpened) {
      document.addEventListener("pointerdown", handleClickOutside);
    }
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, [isDropdownOpened]);

  useEffect(() => {
    if (isDropdownOpened && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    } else {
      setDropdownPosition(null);
    }
  }, [isDropdownOpened]);

  useEffect(() => {
    const el = document.getElementById("portal-root");
    setPortalRoot(el);
  }, []);

  const { top, left, width } = dropdownPosition || {};

  return (
    <div ref={containerRef} className="flex flex-col gap-2 p-0">
      {showDropdown &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{ top, left, width }}
            className={cn(
              "absolute z-50 w-full min-w-0 max-w-screen min-h-[100px] max-h-[360px] flex flex-col",
              "items-center justify-center rounded-md border border-stroke-primary dark:border-gray-600",
              "divide-y divide-stroke-primary dark:divide-gray-600 bg-white dark:bg-gray-850 shadow-navItem text-content-primary",
              dropdownClassNames
            )}
          >
            <TaggingComponent
              contentType={contentType}
              query={query}
              onChange={setQuery}
              onSelect={onSelect}
              uppercaseLabels
              keyForTypes={keyForTypes}
              excludedIds={excludedIds}
              showNewEntityOptions
            />
          </div>,
          portalRoot
        )}

      <div className="flex flex-wrap items-center gap-1 px-1">
        {renderTags(selectedTags, onChangeTags, onRemoveTag)}

        <View.Condition if={onOpenDropdown && selectedTags.length > 0}>
          <Button
            buttonType="TRANSPARENT"
            onClick={(e) => {
              e.preventDefault(); // Prevent closing global dialog
              onOpenDropdown();
            }}
            PrefixIcon={PlusIcon}
            className="w-7 h-7 p-0 text-content-secondary dark:hover:bg-gray-700 dark:text-invertedPrimary80 rounded-md"
            isIconOnly
          />
        </View.Condition>
      </div>
    </div>
  );
};
